import { useMemo } from "react";

/**
 * Extract index name from symbol
 * @param {string} symbol - e.g., "NSE:BANKNIFTY25NOV58000PE"
 * @returns {string|null} - e.g., "BANKNIFTY" or null for non-index symbols
 */
function getIndexFromSymbol(symbol, strikeLabel) {
  try {
    if (!symbol) return null;
    const symbolUpper = symbol.toUpperCase();

    // Remove NSE: prefix if exists and normalize to uppercase
    const cleanSymbol = symbolUpper.replace("NSE:", "");

    // Exclude MCX, NFO, BFO symbols, and all FUT symbols - don't track them in OrdersView
    if (symbolUpper.includes("MCX") || symbolUpper.includes("NFO") || symbolUpper.includes("BFO") || symbolUpper.endsWith("FUT")) return null;

    let base = null;
    // Check for longer index names first to avoid false matches
    if (cleanSymbol.includes("BANKNIFTY")) base = "BANKNIFTY";
    else if (cleanSymbol.includes("FINNIFTY") || cleanSymbol.includes("FIN NIFTY")) base = "FINNIFTY";
    else if (cleanSymbol.includes("MIDCPNIFTY") || cleanSymbol.includes("MIDCP")) base = "MIDCPNIFTY";
    else if (cleanSymbol.includes("SENSEX") || cleanSymbol.includes("BSX")) base = "SENSEX";
    else if (cleanSymbol.includes("NIFTY")) base = "NIFTY";

    // Only append strike label if it's a real numbered leg (e.g. "strike_2", "strike_3")
    // Exclude "N/A", "strike_1" (treated as the default/base column), empty values
    const isRealStrike = strikeLabel &&
      strikeLabel.toLowerCase() !== "n/a" &&
      strikeLabel !== "strike_1" &&
      /^strike_\d+$/i.test(strikeLabel);

    if (base && isRealStrike) {
      return `${base}_${strikeLabel.toUpperCase()}`;
    }
    return base;
  } catch (e) {
    console.error("Error extracting index from symbol:", symbol, e);
    return null;
  }
}



/**
 * Calculate MODE (most frequent value) from an array of numbers
 * @param {number[]} numbers
 * @returns {number}
 */
function calculateMode(numbers) {
  if (!numbers || numbers.length === 0) return 0;

  const frequency = {};
  let maxFreq = 0;
  let mode = numbers[0];

  numbers.forEach((num) => {
    frequency[num] = (frequency[num] || 0) + 1;
    if (frequency[num] > maxFreq) {
      maxFreq = frequency[num];
      mode = num;
    }
  });

  return mode;
}

/**
 * Calculate base quantity for each unique symbol+strategy combination (for OrderComparisonModal)
 * @param {Array} orders - Orders data
 * @returns {Object} - { "symbol_strategy": baseQty, ... }
 */
function calculateSymbolStrategyBaseQtyFromOrders(orders) {
  const symbolStrategyQtyMap = {};

  // Filter only BUY orders (side === 1) - match client-old logic exactly
  const buyOrders = orders.filter((order) => order.side === 1);

  buyOrders.forEach((order) => {
    const index = getIndexFromSymbol(order.symbol, order.strike_label);

    // Skip non-index orders
    if (!index) return;

    const key = `${order.symbol}_${order.strategy || "NO_STRATEGY"}`;

    if (!symbolStrategyQtyMap[key]) {
      symbolStrategyQtyMap[key] = {
        symbol: order.symbol,
        strategy: order.strategy,
        index,
        clientQuantities: {},
      };
    }

    // Sum quantities by client_id
    const clientId = order.clientId || order.client_id;
    const qty = Math.abs(parseFloat(order.qty || 0));

    if (!symbolStrategyQtyMap[key].clientQuantities[clientId]) {
      symbolStrategyQtyMap[key].clientQuantities[clientId] = 0;
    }
    symbolStrategyQtyMap[key].clientQuantities[clientId] += qty;
  });

  // Calculate MODE for each symbol+strategy combination
  const baseQtyMap = {};
  Object.entries(symbolStrategyQtyMap).forEach(([key, data]) => {
    const quantities = Object.values(data.clientQuantities);
    const mode = calculateMode(quantities);
    baseQtyMap[key] = {
      symbol: data.symbol,
      strategy: data.strategy,
      index: data.index,
      baseQty: mode,
      clients: Object.keys(data.clientQuantities).length,
      allQtys: quantities,
    };
  });

  return baseQtyMap;
}

/**
 * Group and sum buy orders by client and index
 * @param {Array} orders - Orders data
 * @returns {Object} - Grouped buy orders
 */
function groupBuyOrdersByClientIndex(orders) {
  const grouped = {};

  // Filter only BUY orders (side === 1) - match client-old logic exactly
  const buyOrders = orders.filter((order) => order.side === 1);

  buyOrders.forEach((order) => {
    const index = getIndexFromSymbol(order.symbol, order.strike_label);

    // Skip MCX or null indexes
    if (!index || index === null) {
      return;
    }

    // Handle both clientId (camelCase) and client_id (snake_case)
    const clientId = order.clientId || order.client_id;
    const key = `${clientId}_${index}`;

    if (!grouped[key]) {
      grouped[key] = {
        client_id: clientId,
        index,
        qty: 0,
        orders: [],
      };
    }

    grouped[key].qty += order.qty || 0; // Match client-old logic exactly
    grouped[key].orders.push(order);
  });

  return grouped;
}

/**
 * Calculate base quantity (MODE) for each index from buy orders
 * This uses the same logic as positions: group by client+index, then find MODE
 * @param {Object} groupedOrders
 * @returns {Object} - { NIFTY: 100, BANKNIFTY: 150, ... }
 */
function calculateBaseQtyMapFromOrders(groupedOrders) {
  const indexQuantities = {};

  // Group quantities by index
  Object.values(groupedOrders).forEach((group) => {
    if (!indexQuantities[group.index]) {
      indexQuantities[group.index] = [];
    }
    indexQuantities[group.index].push(group.qty);
  });

  // Calculate MODE for each index
  const baseQtyMap = {};
  Object.keys(indexQuantities).forEach((index) => {
    const mode = calculateMode(indexQuantities[index]);
    baseQtyMap[index] = mode;
  });

  return baseQtyMap;
}

/**
 * Determine color based on validation rules for orders
 * @param {number} actualQty - Actual order quantity
 * @param {number} expectedQty - Expected quantity (multiplier × base qty)
 * @param {boolean} hasOrders - Whether user has placed orders
 * @returns {string} - 'green' or 'red'
 */
function determineOrderColor(actualQty, expectedQty, hasOrders) {
  // No orders placed - red
  if (!hasOrders) {
    return "red";
  }

  // Has orders but quantity doesn't match - red
  if (actualQty !== expectedQty) {
    return "red";
  }

  // Has orders and quantity matches - green
  return "green";
}

/**
 * Hook for Orders View data processing
 * @param {Array} orders - Orders data from API
 * @param {Array} userMappings - User mapping data with multipliers
 * @param {number} ordersUpdateVersion - Version counter to force re-computation
 * @param {Array} users - Global users array fetched from backend containing full details like is_pledge_plus
 * @param {Array} multiAssetUnderlyings - Array of valid multi-asset underlyings
 * @returns {Object} - { data, baseQtyMap, activeIndexes }
 */
export function useOrdersView(orders, userMappings, ordersUpdateVersion, users, multiAssetUnderlyings = ["NIFTY", "BANKNIFTY"]) {
  const orderViewData = useMemo(() => {
    if (!userMappings || userMappings.length === 0) {
      return {
        data: [],
        allClientsData: [],
        baseQtyMap: {},
        activeIndexes: [],
        symbolStrategyBaseQty: {},
        buyOrdersCount: 0,
      };
    }

    // Step 1: Discover all possible strike indexes from user mappings
    const strikeIndexes = new Set();
    userMappings.forEach(u => {
      Object.keys(u).forEach(key => {
        if (key.includes('_strike_') && key.endsWith('_multiplier')) {
          strikeIndexes.add(key.replace('_multiplier', '').toUpperCase());
        }
      });
    });

    const baseIndexes = ["NIFTY", "BANKNIFTY", "FINNIFTY", "MIDCPNIFTY", "SENSEX"];
    const allPossibleIndexes = Array.from(new Set([...baseIndexes, ...strikeIndexes]));

    // Step 2: Group buy orders
    const groupedBuyOrders = groupBuyOrdersByClientIndex(orders || []);
    const symbolStrategyBaseQty = calculateSymbolStrategyBaseQtyFromOrders(orders || []);

    const validMaUnderlyings = (Array.isArray(multiAssetUnderlyings) && multiAssetUnderlyings.length > 0)
      ? multiAssetUnderlyings.map(u => String(u).toUpperCase().trim())
      : ["NIFTY", "BANKNIFTY"];

    // Calculate separate base qty maps for Optimus and Optimus Multi-Asset
    const optimusBaseQtyMap = {};
    const optimusMaBaseQtyMap = {};
    const baseQtyMap = {};
    const optimusIndexQtys = {};
    const maIndexQtys = {};

    Object.values(groupedBuyOrders).forEach((group) => {
      const clientId = group.client_id;
      const index = group.index;
      const rawQty = group.qty;
      if (!rawQty || rawQty <= 0) return;

      const userMapping = userMappings.find((u) => u.client_id === clientId);
      const matchedUser = users?.find((u) => (u.client_id || u.clientId)?.toString().trim() === clientId?.toString().trim());
      const productVal = (matchedUser?.product || userMapping?.product || "").toLowerCase();
      const isMultiAsset = productVal === 'multi_asset' || productVal === 'multiasset';
      const multiAssetMul = Number(userMapping?.multi_asset_multiplier ?? userMapping?.multiasset_multiplier ?? matchedUser?.multi_asset_multiplier ?? matchedUser?.multiasset_multiplier ?? 0);
      const effectiveMaMul = multiAssetMul > 0 ? multiAssetMul : (parseFloat(userMapping?.multiplier || matchedUser?.multiplier) || 1);

      const parts = index.split('_');
      const baseIndex = parts[0].toUpperCase();
      const isStrike2OrAbove = parts.length > 1 && !index.toLowerCase().includes('_strike_1');
      const key = parts.length > 1 ? `${index.toLowerCase()}_multiplier` : (index === "NIFTY" ? "multiplier" : `${index.toLowerCase()}_multiplier`);

      let multiplier = 0;
      if (isMultiAsset) {
        if (isStrike2OrAbove) multiplier = 0;
        else if (validMaUnderlyings.includes(baseIndex)) multiplier = effectiveMaMul;
        else multiplier = 0;
      } else {
        multiplier = parseFloat(userMapping?.[key]) || 0;
      }

      if (multiplier < 1) return;

      const normQty = rawQty / multiplier;
      const upperIdx = index.toUpperCase();
      if (isMultiAsset) {
        if (!maIndexQtys[upperIdx]) maIndexQtys[upperIdx] = [];
        maIndexQtys[upperIdx].push(normQty);
      } else {
        if (!optimusIndexQtys[upperIdx]) optimusIndexQtys[upperIdx] = [];
        optimusIndexQtys[upperIdx].push(normQty);
      }
    });

    Object.keys(optimusIndexQtys).forEach((index) => {
      optimusBaseQtyMap[index] = calculateMode(optimusIndexQtys[index]);
    });
    Object.keys(maIndexQtys).forEach((index) => {
      optimusMaBaseQtyMap[index] = calculateMode(maIndexQtys[index]);
    });

    allPossibleIndexes.forEach((index) => {
      const upper = index.toUpperCase();
      const optVal = optimusBaseQtyMap[upper];
      const maVal = optimusMaBaseQtyMap[upper];
      if (optVal) baseQtyMap[index] = optVal;
      else if (maVal) baseQtyMap[index] = maVal;
    });

    // Step 3: Total Buy Order Count
    const buyOrderCount = (orders || []).filter((o) => {
      const isBuy = o.side === 1;
      const isValidStatus = String(o.status_no) === "6" || String(o.status_no) === "4";
      const index = getIndexFromSymbol(o.symbol, o.strike_label);
      return isBuy && isValidStatus && index;
    }).length;

    // Step 4: Client → Index aggregation
    const clientIndexMap = {};
    Object.values(groupedBuyOrders).forEach((orderGroup) => {
      const clientId = orderGroup.client_id;
      const index = orderGroup.index;
      if (!clientIndexMap[clientId]) clientIndexMap[clientId] = {};
      if (!clientIndexMap[clientId][index]) clientIndexMap[clientId][index] = { qty: 0 };
      clientIndexMap[clientId][index].qty += orderGroup.qty;
    });

    const indexesWithOrders = new Set();
    Object.values(clientIndexMap).forEach((clientData) => {
      Object.keys(clientData).forEach((index) => indexesWithOrders.add(index));
    });

    // Step 5: Prepare table rows
    const data = [];
    const allClientsData = [];

    const clientBrokerMap = {};
    userMappings.forEach(u => {
      if (!clientBrokerMap[u.client_id]) clientBrokerMap[u.client_id] = new Set();
      clientBrokerMap[u.client_id].add((u.broker || "").toLowerCase());
    });

    userMappings.forEach((userMapping) => {
      if (userMapping.status !== "active") return;
      const clientId = userMapping.client_id;
      const matchedUser = users?.find(u => u.client_id === clientId || u.clientId === clientId);
      const isPledgePlus = matchedUser?.is_pledge_plus || userMapping.is_pledge_plus || matchedUser?.is_pledgeplus_mini || userMapping.is_pledgeplus_mini;

      if (isPledgePlus === true || String(isPledgePlus).toLowerCase() === "true") {
        const hasAnyMult = (Number(userMapping.multi_asset_multiplier ?? userMapping.multiasset_multiplier ?? 0) >= 1) || allPossibleIndexes.some(idx => {
          const parts = idx.split('_');
          const key = parts.length > 1 ? `${idx.toLowerCase()}_multiplier` : (idx === "NIFTY" ? "multiplier" : `${idx.toLowerCase()}_multiplier`);
          return (parseFloat(userMapping[key]) || 0) >= 1;
        });
        if (!hasAnyMult) return;
      }

      const broker = (userMapping.broker || "").toLowerCase();
      if (broker === "zerodha") return;

      const hasXts = clientBrokerMap[clientId]?.has("dhan_xts");
      const isSs = broker === "dhan_ss";
      if (hasXts && isSs) return;

      const productVal = (matchedUser?.product || userMapping.product || "").toLowerCase();
      const multiAssetMul = Number(userMapping.multi_asset_multiplier ?? userMapping.multiasset_multiplier ?? 0);
      const isMultiAsset = productVal === 'multi_asset' || productVal === 'multiasset';
      const effectiveMaMul = multiAssetMul > 0 ? multiAssetMul : (parseFloat(userMapping.multiplier) || 1);

      const rowData = {
        clientId,
        clientName: userMapping.name,
        broker: userMapping.broker,
        indexCount: 0,
        hasOrdersCount: 0,
        multi_asset_multiplier: multiAssetMul,
        effectiveMaMul,
        isMultiAsset,
        product: isMultiAsset ? 'multi_asset' : 'optimus',
      };

      let hasAnyDataInRow = false;
      let hasAnyMultiplierInRow = false;

      allPossibleIndexes.forEach((index) => {
        const parts = index.split('_');
        const baseIndex = parts[0].toUpperCase();
        const isStrike2OrAbove = parts.length > 1 && !index.toLowerCase().includes('_strike_1');
        const key = parts.length > 1 ? `${index.toLowerCase()}_multiplier` : (index === "NIFTY" ? "multiplier" : `${index.toLowerCase()}_multiplier`);
        let multiplier = parseFloat(userMapping[key]) || 0;

        // Multi-asset override: when isMultiAsset is true, it ONLY applies to Strike 1 / base index of validMaUnderlyings
        if (isMultiAsset) {
          if (isStrike2OrAbove) {
            multiplier = 0; // Strike 2 and Strike 3 are NEVER considered in Multi-Asset mode
          } else if (validMaUnderlyings.includes(baseIndex)) {
            multiplier = effectiveMaMul;
          } else {
            multiplier = 0;
          }
        }
        
        if (multiplier < 1) return;
        hasAnyMultiplierInRow = true;

        const upperIdx = index.toUpperCase();
        const regimeBaseQty = isMultiAsset
          ? (optimusMaBaseQtyMap[upperIdx] || optimusMaBaseQtyMap[index] || 0)
          : (optimusBaseQtyMap[upperIdx] || optimusBaseQtyMap[index] || 0);

        const expectedQty = multiplier * regimeBaseQty;
        const isIndexActive = indexesWithOrders.has(index);

        if (isIndexActive && clientIndexMap[clientId]?.[index]) {
          const idxData = clientIndexMap[clientId][index];
          const color = determineOrderColor(idxData.qty, expectedQty, true);
          hasAnyDataInRow = true;
          rowData.indexCount++;
          rowData[index.toLowerCase()] = {
            multiplier,
            qty: idxData.qty,
            color,
            expectedQty,
            hasOrders: true,
          };
          rowData.hasOrdersCount++;
        } else {
          rowData[index.toLowerCase()] = {
            multiplier,
            qty: 0,
            color: "red",
            expectedQty,
            hasOrders: false,
          };
        }
      });


      // Calculate Total Multiplier
      let totalMultiplier = 0;
      allPossibleIndexes.forEach((index) => {
        const lowerIndex = index.toLowerCase();
        const indexDataRow = rowData[lowerIndex];
        if (indexDataRow) {
          totalMultiplier += (indexDataRow.multiplier || 0);
        }
      });
      rowData.totalMultiplier = totalMultiplier;

      if (hasAnyDataInRow) data.push(rowData);
      if (hasAnyMultiplierInRow) allClientsData.push(rowData);
    });

    // Step 6: Sorting & Final Prep
    const sortRows = (a, b) => {
      if (a.hasOrdersCount !== b.hasOrdersCount) return b.hasOrdersCount - a.hasOrdersCount;
      return b.indexCount - a.indexCount;
    };
    data.sort(sortRows);
    allClientsData.sort(sortRows);

    const indexBaseNames = ["nifty", "banknifty", "finnifty", "midcpnifty", "sensex"];
    const sortIndexes = (a, b) => {
      const baseA = a.split('_')[0].toLowerCase();
      const baseB = b.split('_')[0].toLowerCase();
      if (baseA !== baseB) return indexBaseNames.indexOf(baseA) - indexBaseNames.indexOf(baseB);
      return a.localeCompare(b);
    };

    const activeIndexes = allPossibleIndexes
      .map(i => i.toLowerCase())
      .filter(k => data.some(row => row[k]?.hasOrders === true))
      .sort(sortIndexes);

    const allClientsActiveIndexes = allPossibleIndexes
      .map(i => i.toLowerCase())
      .filter(k => allClientsData.some(row => row[k] !== undefined))
      .sort(sortIndexes);

    return {
      data,
      allClientsData,
      baseQtyMap,
      optimusBaseQtyMap,
      optimusMaBaseQtyMap,
      activeIndexes,
      allClientsActiveIndexes,
      symbolStrategyBaseQty,
      buyOrdersCount: buyOrderCount,
    };
  }, [orders, userMappings, ordersUpdateVersion, users, multiAssetUnderlyings]);

  return orderViewData;
}
